import useControlledState from '@quid/react-use-controlled-state';
import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { CommonComponentProps, StylableComponentProps } from '@clds/common-definitions';
import { asStyledComponent } from '@clds/component-enhancer';
import { FieldBase } from './FieldBase/FieldBase';
import { OverflowContainer } from './OverflowContainer/OverflowContainer';
import { isPlaintextOption, SearchQuery } from './SearchField.helper';
import { SearchFieldButtons, SearchFieldButtonsProps } from './SearchFieldButtons/SearchFieldButtons';
import { useSearchChips } from './useSearchChips';

export interface SearchFieldProps
  extends CommonComponentProps<typeof FieldBase>,
    StylableComponentProps,
    Pick<SearchFieldButtonsProps, 'advancedButtonMode' | 'onAdvancedButtonClick'> {
  /** if passed, the component will work in controlled mode */
  value?: SearchQuery;
  /** if passed, the component will work in uncontrolled mode */
  defaultValue?: SearchQuery;
  /** if passed, the component will work in controlled mode and any change will result in calling onChange callback */
  onChange?: (query: SearchQuery) => void;
  onNewTextChip?: (text: string) => void;
  onClear?: () => void;
  hideAdvancedButtonMode?: boolean;
}

/**
 * A component that renders cookbook and transformations search field
 */
export const SearchField = asStyledComponent<FC<SearchFieldProps>>(
  ({ value, defaultValue, onChange, rootProps, advancedButtonMode, onAdvancedButtonClick, className, onNewTextChip, onClear, hideAdvancedButtonMode }) => {
    const [controlledValue, setControlledValue] = useControlledState(defaultValue, value, onChange);
    const [text, setText] = useState('');

    const chips = useSearchChips(controlledValue || [], setControlledValue);

    const handleEnter = useCallback(() => {
      const textIsNotEmpty = text.trim() !== '';
      const existingTextQuery = controlledValue?.find((option) => isPlaintextOption(option) && option.text === text);

      if (textIsNotEmpty && !existingTextQuery) {
        onNewTextChip?.(text);
        setControlledValue([...(controlledValue || []), { text }]);
      }
    }, [text, onNewTextChip, controlledValue, setControlledValue]);

    const clearValues = useCallback(() => {
      setControlledValue([]);
      onClear?.();
    }, [setControlledValue, onClear]);

    const backspaceHandler = () => {
      if (controlledValue) {
        const textIsEmpty = text.trim() === '';
        if (textIsEmpty && controlledValue.length > 0) {
          setControlledValue(controlledValue.slice(0, -1));
        }
      }
    };

    const searchFieldButtonsProps = useMemo(
      () => ({
        ...(hideAdvancedButtonMode
          ? {}
          : {
              advancedButtonMode,
              onAdvancedButtonClick,
            }),
        onSearchButtonClick: handleEnter,
        onClearButtonClick: clearValues,
        isEmptySearchField: !controlledValue?.length,
      }),
      [advancedButtonMode, onAdvancedButtonClick, hideAdvancedButtonMode, handleEnter, controlledValue, clearValues]
    );

    useEffect(() => {
      setText('');
    }, [controlledValue]);

    return (
      <FieldBase
        value={text}
        onChange={setText}
        onEnter={handleEnter}
        onBackspace={backspaceHandler}
        beforeDecorator={<OverflowContainer>{chips}</OverflowContainer>}
        afterDecorator={<SearchFieldButtons {...searchFieldButtonsProps} />}
        {...rootProps}
        rootProps={{ className }}
      />
    );
  }
);
